import DialogComponent from 'components/Dialog';
import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {DialogTypes} from 'common/names';
import FormikBrowseFileInput from 'components/inputs/FormikBrowseFileInput';
import FormikDropdown from 'components/inputs/FormikDropdown';
import FormikNumberField from 'components/inputs/FormikNumberField';
import LastUpdatedLabel from 'components/LastUpdatedLabel';
import Panel from 'components/Panel';
import {enums} from 'types/enums';
import {ILogFileSettings, LogFileSettings} from 'types/LogFileSettings';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import {useGetApi} from 'common/hooks/useGetApi';
import api from 'api';
import Loader from 'components/Loader';
import {usePostApi} from 'common/hooks/usePostApi';
import Grid from '@mui/material/Grid';
import {Offset} from 'components/Offset';
import Typography from '@mui/material/Typography';
import TestDirectory from 'features/layout/CommonPage/panels/TransportPanel/components/TestDirectory';
import Formik from 'components/hook-form/Formik';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';

const logLevelOptions = enums.minimumLevel.options.filter(
   (option) =>
      option.id !== enums.minimumLevel.enum.LOG_NOTHING &&
      option.id !== enums.minimumLevel.enum.LOG_NOTIFICATIONS
);

type LogSettingsDialogProps = {
   onClose: () => void;
};

const LogSettingsForm = ({onClose}: LogSettingsDialogProps) => {
   const {t} = useTranslation();
   const getValue = useFieldValueGetter<ILogFileSettings>();

   const location = getValue('location');
   const timestamp = getValue('timestamp');
   const lastUser = getValue('lastUser');
   return (
      <>
         <Panel>
            <Grid container>
               <Grid item container spacing={2} alignItems={'center'}>
                  <Grid item xs={9}>
                     <FormikBrowseFileInput
                        autoFocus
                        name="location"
                        placeholder={t('File Location')}
                        directoriesOnly
                     />
                  </Grid>
                  <Grid item xs={3}>
                     <TestDirectory header={t('Location Test Result')} directoryPath={location} />
                  </Grid>
               </Grid>
               <Grid item xs={4}>
                  <FormikDropdown
                     name="reportingLevel"
                     placeholder={t('Logging Level')}
                     options={logLevelOptions}
                  />
               </Grid>
               <Offset xs={8} />
               <Grid item xs={6}>
                  <Panel name={t('Archival and Retention')}>
                     <FormikNumberField
                        name="maxSize"
                        placeholder={t('Archive Log Files Over')}
                        min={1}
                        InputProps={{
                           endAdornment: <Typography variant={'body2'}>{t('MBytes')}</Typography>,
                        }}
                     />
                     <FormikNumberField
                        name="maxAge"
                        placeholder={t('Archive Log Files Every')}
                        min={1}
                        InputProps={{
                           endAdornment: <Typography variant={'body2'}>{t('Days')}</Typography>,
                        }}
                     />
                     <FormikNumberField
                        name="daysToKeep"
                        placeholder={t('Retain Archived Log Files For')}
                        min={1}
                        InputProps={{
                           endAdornment: <Typography variant={'body2'}>{t('Days')}</Typography>,
                        }}
                     />
                  </Panel>
               </Grid>
               <Offset xs={6} />
               <Grid item xs={4}>
                  <FormikNumberField
                     name="performanceFrequency"
                     placeholder={t('Performance Logging')}
                     min={0}
                     inputProps={{style: {textAlign: 'right'}}}
                     InputProps={{
                        startAdornment: <Typography variant={'body2'}>{t('Every')}</Typography>,
                        endAdornment: <Typography variant={'body2'}>{t('Minutes')}</Typography>,
                     }}
                  />
               </Grid>
               <Grid item xs={12}>
                  <LastUpdatedLabel date={timestamp} author={lastUser} />
               </Grid>
            </Grid>
         </Panel>
         <FormFooterButtons cancel={onClose} submitLabel={t('Save')} />
      </>
   );
};

const LogSettingsDialog = ({onClose}: LogSettingsDialogProps) => {
   const {request: fetchLogSettings, state: settings, loading} = useGetApi<ILogFileSettings>(
      api.logFileSettings
   );
   const {request: saveLogSettings} = usePostApi(api.logFileSettings, {onSuccess: onClose});

   useEffect(() => {
      fetchLogSettings();
   }, [fetchLogSettings]);

   const saveSettings = (values: ILogFileSettings) => {
      saveLogSettings(JSON.stringify(values));
   };

   return (
      <DialogComponent
         maxWidth="md"
         header={DialogTypes.LogSettingsDialog.dialogName}
         close={onClose}
      >
         {loading || !settings ? (
            <Loader />
         ) : (
            <Formik
               initialValues={LogFileSettings.cast(settings) as ILogFileSettings}
               validationSchema={LogFileSettings}
               onSubmit={saveSettings}
               enableReinitialize
            >
               <LogSettingsForm onClose={onClose} />
            </Formik>
         )}
      </DialogComponent>
   );
};

export default LogSettingsDialog;
